import { useMemo } from 'react';
import type { LucideIcon } from 'lucide-react';
import { defaultFilter } from 'cmdk';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
	CommandDialog,
	CommandInput,
	CommandList,
	CommandEmpty,
	CommandGroup,
	CommandItem,
	CommandShortcut
} from '~/components/ui/command';
import { useCommandPaletteStore } from '~/lib/store/command-palette';
import { visibleMenu } from '~/lib/menu';
import { useHasAccess } from '~/lib/auth/use-has-access';
import { useMenuFeatures } from '~/hooks/use-menu-features';

interface SearchEntry {
	id: string;
	label: string;
	path: string;
	icon: LucideIcon;
	/** Parent menu label, shown dimmed for disambiguation (several pages share a label, e.g. "Templates"). */
	parentLabel?: string;
	/** Extra match tokens (parent + section labels + path) so a section/parent term surfaces its pages too. */
	keywords: string[];
	/** Unique cmdk value — must be unique even when labels collide, or selection breaks. */
	value: string;
}

interface SearchGroup {
	id: string;
	heading: string;
	entries: SearchEntry[];
}

/** Strip diacritics + lowercase so Greek users can match accented labels (e.g. "Δωροκάρτες") without typing accents. */
const stripDiacritics = (value: string) =>
	value
		.normalize('NFD')
		.replace(/\p{Diacritic}/gu, '')
		.toLowerCase();

/** cmdk's fuzzy scorer, made diacritic-insensitive on both the haystack and the query. */
const diacriticInsensitiveFilter = (value: string, search: string, keywords?: string[]) =>
	defaultFilter(stripDiacritics(value), stripDiacritics(search), keywords?.map(stripDiacritics));

/**
 * ⌘K command palette: fuzzy search over the navigation menu. Entries are the
 * routable leaves of `~/lib/menu` (every child + top-level items with a path),
 * grouped by section. Matching runs against the resolved i18n labels (cmdk's
 * built-in fuzzy filter) plus parent/section labels and the path as keywords, so
 * searching either a page name ("Skroutz") or its parent/section ("orders")
 * surfaces it. Scope- and feature-flag-gated pages are hidden exactly like the sidebar.
 */
export function CommandPalette() {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const hasAccess = useHasAccess();
	const hasFeature = useMenuFeatures();
	const open = useCommandPaletteStore(s => s.open);
	const setOpen = useCommandPaletteStore(s => s.setOpen);

	const groups = useMemo<SearchGroup[]>(() => {
		// i18n.language referenced so memo recomputes labels on language switch.
		void i18n.language;
		// The same filter the sidebar applies, from the same place — the palette must not be able to offer a
		// page the rail has hidden, nor hide one it shows.
		return visibleMenu(hasAccess, hasFeature)
			.map(section => {
				const heading = t(section.labelKey);
				const entries = section.items.flatMap<SearchEntry>(item => {
					const parentLabel = t(item.labelKey);
					const own: SearchEntry[] = item.path
						? [
								{
									id: item.id,
									label: parentLabel,
									path: item.path,
									icon: item.icon,
									keywords: [heading, item.path],
									value: `${parentLabel} ${item.path}`
								}
							]
						: [];
					const kids = (item.children ?? []).map<SearchEntry>(child => {
						const label = t(child.labelKey);
						return {
							id: child.id,
							label,
							path: child.path,
							icon: item.icon,
							parentLabel,
							keywords: [parentLabel, heading, child.path],
							value: `${label} ${parentLabel} ${child.path}`
						};
					});
					return [...own, ...kids];
				});
				return { id: section.id, heading, entries };
			})
			.filter(group => group.entries.length > 0);
	}, [t, i18n.language, hasAccess, hasFeature]);

	const handleSelect = (path: string) => {
		setOpen(false);
		navigate(path);
	};

	return (
		<CommandDialog
			open={open}
			onOpenChange={setOpen}
			title={t('shell.search.title')}
			description={t('shell.search.placeholder')}
			filter={diacriticInsensitiveFilter}>
			<CommandInput placeholder={t('shell.search.placeholder')} />
			<CommandList>
				<CommandEmpty>{t('shell.search.empty')}</CommandEmpty>
				{groups.map(group => (
					<CommandGroup key={group.id} heading={group.heading}>
						{group.entries.map(entry => {
							const Icon = entry.icon;
							return (
								<CommandItem key={entry.id} value={entry.value} keywords={entry.keywords} onSelect={() => handleSelect(entry.path)}>
									<Icon className="text-foreground-subtle" />
									<span>{entry.label}</span>
									{entry.parentLabel && <CommandShortcut>{entry.parentLabel}</CommandShortcut>}
								</CommandItem>
							);
						})}
					</CommandGroup>
				))}
			</CommandList>
		</CommandDialog>
	);
}
